import { construct, invoke } from "./invoke.js";
import { createOnAndOffNamespaceListenersWithCompatiblityForLegacyEvents, } from "./utils.js";
/**
 * @public
 */
/**
 * Creates a new BrowserWindow.
 *
 * @param options - BrowserWindow constructor options.
 * @returns Identifier for the newly created BrowserWindow.
 * @public
 */
export async function create(options) {
    return construct({
        namespace: "BrowserWindow",
        args: [options],
    });
}
/**
 * Destroys the BrowserWindow corresponding to the `ref`.
 *
 * @param options - ref: Reference for the BrowserWindow to destroy
 * @public
 */
export async function destroy({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "destroy",
        args: [],
    });
}
/**
 * Sets a BrowserView on the reference BrowserWindow. If there are
 * other BrowserViews attached, they will be removed from the window.
 *
 * @param options - ref: Reference for the BrowserWindow; viewRef: Reference for the BrowserView to set.
 * @public
 */
export async function setBrowserView({ ref, viewRef, }) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setBrowserView",
        args: [viewRef],
    });
}
/**
 * Adds a BrowserView to the reference BrowserWindow.
 *
 * @param options - ref: Reference for the BrowserWindow; viewRef: Reference for the BrowserView to add.
 * @public
 */
export async function addBrowserView({ ref, viewRef, }) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "addBrowserView",
        args: [viewRef],
    });
}
/**
 * Removes a BrowserView from the reference BrowserWindow.
 *
 * @param options - ref: Reference for the BrowserWindow; viewRef: Reference for the BrowserView to remove.
 * @public
 */
export async function removeBrowserView({ ref, viewRef, }) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "removeBrowserView",
        args: [viewRef],
    });
}
/**
 * Gets the width and height of the reference browser window
 *
 * @param options - ref: Reference for the BrowserWindow.
 * @public
 */
export async function getSize({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "getSize",
        args: [],
    });
}
/**
 * Changes the title of the reference BrowserWindow.
 *
 * @param options - ref: Reference for the BrowserWindow; title: Desired title for the BrowserWindow.
 * @public
 */
export async function setTitle({ ref, title }) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setTitle",
        args: [title],
    });
}
/**
 * On a Window with Window Controls Overlay already enabled, this method updates
 * the style of the title bar overlay.
 *
 * **Note:** This API does nothing on Mac or Linux.
 *
 * @platform win32
 * @param options - ref: Reference for the BrowserWindow; options: The configuration for the title bar.
 * @public
 */
export async function setTitleBarOverlay({ ref, options, }) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setTitleBarOverlay",
        args: [options],
    });
}
/**
 * Adds a vibrancy effect to the browser window. Passing null or an empty string will remove the vibrancy effect on the window.
 *
 * **Note:** This API does nothing on Windows or Linux.
 *
 * @platform darwin
 */
export async function setVibrancy({ ref }, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setVibrancy",
        args,
    });
}
/**
 * Sets the opacity of the window. Out of bound number values are clamped to the [0, 1] range.
 *
 * **Note:** This API does nothing on Linux.
 *
 * @platform darwin,windows
 */
export async function setOpacity({ ref }, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setOpacity",
        args,
    });
}
/**
 * Sets the background color of the window.
 *
 * @param options - ref: Reference for the BrowserWindow;
 * @param backgroundColor - The following string value patterns are accepted:
 * - Hex, for example: #ffffff (RGB) or #ffffffff (ARGB).
 * - RGB, for example: rgb(255, 255, 255) or rgba(255, 255, 255, 1.0).
 * - HSL, for example: hsl(200, 20%, 50%) or hsla(200, 20%, 50%, 0.5).
 * - Name, for example: red.
 *
 * @public
 */
export async function setBackgroundColor({ ref }, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setBackgroundColor",
        args,
    });
}
/**
 * Minimize the window.
 *
 * @param options - ref: Reference for the BrowserWindow to minimize
 * @public
 */
export async function minimize({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "minimize",
        args: [],
    });
}
/**
 * Restore the window from minimized state.
 *
 * @param options - ref: Reference for the BrowserWindow to restore
 * @public
 */
export async function restore({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "restore",
        args: [],
    });
}
/**
 * Check if the window is minimized.
 *
 * @param options - ref: Reference for the BrowserWindow to check
 * @public
 */
export async function isMinimized({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "isMinimized",
        args: [],
    });
}
/**
 * Maximize the window.
 *
 * @param options - ref: Reference for the BrowserWindow to maximize
 * @public
 */
export async function maximize({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "maximize",
        args: [],
    });
}
/**
 * Unmaximize the window.
 *
 * @param options - ref: Reference for the BrowserWindow to unmaximize
 * @public
 */
export async function unmaximize({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "unmaximize",
        args: [],
    });
}
/**
 * Check if the window is maximized.
 *
 * @param options - ref: Reference for the BrowserWindow to check
 * @public
 */
export async function isMaximized({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "isMaximized",
        args: [],
    });
}
/**
 * Enter fullscreen mode.
 *
 * @param options - ref: Reference for the BrowserWindow to enter fullscreen
 * @public
 */
export async function enterFullscreen({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setFullScreen",
        args: [true],
    });
}
/**
 * Exit fullscreen mode.
 *
 * @param options - ref: Reference for the BrowserWindow to exit fullscreen
 * @public
 */
export async function exitFullscreen({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setFullScreen",
        args: [false],
    });
}
/**
 * Check if the window is in fullscreen mode.
 *
 * @param options - ref: Reference for the BrowserWindow to check
 * @public
 */
export async function isFullscreen({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "isFullScreen",
        args: [],
    });
}
/**
 * Returns `true` if the current window is set to always be on top of other windows.
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function isAlwaysOnTop({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "isAlwaysOnTop",
        args: [],
    });
}
/**
 * Causes the referenced window to be focussed on the user's desktop.
 *
 * @param options - ref: Reference for the BrowserWindow to focus
 * @public
 */
export async function focus({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "focus",
        args: [],
    });
}
/**
 * Removes the referenced window from focus on the user's desktop.
 *
 * @param options - ref: Reference for the BrowserWindow to blur
 * @public
 */
export async function blur({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "blur",
        args: [],
    });
}
/**
 * @public
 */
export async function areTabsSupported() {
    return window["todesktop"].window.areTabsSupported();
}
/**
 * Creates a new tab and loads the pre-defined app URL.
 *
 * @public
 */
export async function createNewTab() {
    return window["todesktop"].window.createNewTab();
}
/**
 * @param percent - An float between 0-1 will set the progress bar to a
 * certain percentage. e.g. `0.75` will show a progress bar 75% filled.
 *
 * Values above 1 will enable intermediate mode.
 *
 * Values below 0 will disable the progress bar.
 * @public
 */
export async function setProgressBar(percent) {
    return window["todesktop"].window.setProgressBar(percent);
}
/**
 * Returns a boolean indicating whether the window has been destroyed.
 *
 * @param options - ref: Reference for the BrowserWindow
 * @public
 */
export async function isDestroyed({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "isDestroyed",
        args: [],
    });
}
/**
 * Returns a boolean indicating whether the window is visible to the user.
 *
 * @param options - ref: Reference for the BrowserWindow
 * @public
 */
export async function isVisible({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "isVisible",
        args: [],
    });
}
/**
 * Hides the BrowserWindow corresponding to the `ref`.
 *
 * @param options - ref: Reference for the BrowserWindow to hide
 * @public
 */
export async function hide({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "hide",
        args: [],
    });
}
/**
 * Shows the BrowserWindow corresponding to the `ref`.
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function show({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "show",
        args: [],
    });
}
/**
 * Shows the window corresponding to the `ref` but doesn't focus it.
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function showInactive({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "showInactive",
        args: [],
    });
}
/**
 * Close the BrowserWindow corresponding to the `ref`.
 *
 * @param options - ref: Reference for the BrowserWindow to close
 * @public
 */
export async function close({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "close",
        args: [],
    });
}
/**
 * @param options - ref: Reference for the BrowserWindow to show
 * @returns An array of all opened browser windows.
 * @public
 */
export async function getAllWindows() {
    return (await invoke({
        namespace: "BrowserWindow",
        method: "getAllWindows",
        args: [],
        // TODO: Remove eslint-disable-next-line when You found a better type option other than "any"
        // eslint-disable-next-line
        //eslint-disable-next-line
    })).$;
}
/**
 * @param options - ref: Reference for the BrowserWindow
 * @returns `id` of the BrowserWindow.
 * @public
 */
export async function getId({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        property: "id",
    });
}
/**
 *
 * Gets the background color of the window in Hex (#RRGGBB) format.
 * Note: The alpha value is not returned alongside the red, green, and blue values.
 *
 * @param options - ref: Reference for the BrowserWindow;
 *
 * @public
 */
export async function getBackgroundColor({ ref }, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "getBackgroundColor",
        args,
    });
}
/**
 * The title of the native window.
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function getTitle({ ref } = {}) {
    return await invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        property: "title",
    });
}
/**
 * Reloads the web page.
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function reload({ ref } = {}) {
    return await invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "reload",
        args: [],
    });
}
/**
 * Get a WebContents reference for a BrowserWindow. WebContents is responsible
 * for rendering and controlling a web page
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function getWebContents({ ref } = {}) {
    return (await invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        property: "webContents",
    }));
}
/**
 * Sets whether the window should show always on top of other windows. After
 * setting this, the window is still a normal window, not a toolbox window which
 * can not be focused on.
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function setAlwaysOnTop({ ref } = {}, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setAlwaysOnTop",
        args,
    });
}
/**
 * Sets whether the window should be visible on all workspaces.
 *
 * **Note:** This API does nothing on Windows.
 *
 * @platform darwin,linux
 */
export async function setVisibleOnAllWorkspaces({ ref }, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setVisibleOnAllWorkspaces",
        args,
    });
}
/**
 * Changes window icon.
 *
 * @see https://www.electronjs.org/docs/latest/api/browser-window#winseticonicon-windows-linux
 * @public
 */
export async function setIcon({ ref }, image) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setIcon",
        args: [image],
    });
}
/**
 * Prevents the window contents from being captured by other apps.
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function setContentProtection({ ref } = {}, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setContentProtection",
        args,
    });
}
/**
 * Moves window to `x` and `y`.
 *
 * @param options - ref: Reference for the BrowserWindow
 * @public
 */
export async function setPosition({ ref } = {}, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setPosition",
        args,
    });
}
/**
 * Returns the window's current position.
 *
 * @param options - ref: Reference for the BrowserWindow
 * @public
 */
export async function getPosition({ ref } = {}, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "getPosition",
        args,
    });
}
/**
 * Makes the window ignore all mouse events.
 *
 * All mouse events happened in this window will be passed to the window
 * below this window, but if this window has focus, it will still receive
 * keyboard events.
 *
 * @param options - ref: Reference for the BrowserWindow
 * @public
 */
export async function setIgnoreMouseEvents({ ref } = {}, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setIgnoreMouseEvents",
        args,
    });
}
/**
 * Sets the position of the window buttons.
 *
 * @param options - ref: Reference for the BrowserWindow
 * @public
 */
export async function setWindowButtonPosition({ ref } = {}, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setWindowButtonPosition",
        args,
    });
}
/**
 * Returns the position of the window buttons.
 *
 * @param options - ref: Reference for the BrowserWindow
 * @public
 */
export async function getWindowButtonPosition({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "getWindowButtonPosition",
        args: [],
    });
}
/**
 * Sets whether the window can be focused.
 *
 * @param options - ref: Reference for the BrowserWindow
 * @public
 */
export async function setFocusable({ ref } = {}, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setFocusable",
        args,
    });
}
/**
 * Sets whether the window can be manually closed by user.
 *
 * @param options - ref: Reference for the BrowserWindow
 * @param closable - Whether the window can be manually closed by the user
 * @platform darwin,win32
 * @public
 */
export async function setClosable({ ref } = {}, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setClosable",
        args,
    });
}
/**
 * Starts or stops flashing the window to attract user's attention.
 *
 * @remarks
 * On macOS, this will flash the dock icon continuously when flag is true.
 * On Windows, this will flash the taskbar button.
 * On Linux, this has no effect.
 *
 * @param options - ref: Reference for the BrowserWindow
 * @param flag - Whether to start or stop flashing the window
 * @platform darwin,win32
 * @public
 */
export async function flashFrame({ ref } = {}, flag) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "flashFrame",
        args: [flag],
    });
}
const _legacyNativeWindowEventMappings = {
    enterFullscreen: "window.enter-full-screen",
    exitFullscreen: "window.leave-full-screen",
};
const listeners = createOnAndOffNamespaceListenersWithCompatiblityForLegacyEvents(_legacyNativeWindowEventMappings, "BrowserWindow");
/**
 * @public
 */
export const on = listeners.on;
/**
 * @public
 */
export const removeAllListeners = listeners.removeAllListeners;
